import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import qrcode from "qrcode-generator";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import { fmtDate, cn, TYPE_COLOR } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, MapPin, Users, ArrowLeft, Award, QrCode, Clock, Star, ExternalLink, MessageCircle, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Badge from "@/components/Badge";

export default function EventDetail() {
  const { id: pathId } = useParams();
  const [sp]          = useSearchParams();
  // Email ticket links use the obfuscated path and carry the event in ?id=.
  const id            = pathId || sp.get("id") || "";
  const regParam      = sp.get("reg")   || "";
  const tokenParam    = sp.get("token") || "";

  // get() resolves (not rejects) on {success:false}, so check the flag and fall
  // back to the standalone lookup ourselves instead of relying on .catch().
  const ev = useFetch(async () => {
    const r = await api.getEventProfile(id, tokenParam).catch(() => null);
    if (r && r.success !== false) return r;
    const s = await api.getStandaloneEvent(id).catch(() => null);
    return (s && s.success !== false) ? s : (r || s);
  }, [id]);
  const data = ev.data;
  // Published gallery isn't part of the event profile — fetch it separately.
  const galleryQ = useFetch(() => id ? api.gallery(id).then(r => r?.photos || []).catch(() => []) : Promise.resolve([]), [id]);
  const galleryPhotos = galleryQ.data || [];
  const [showReg, setShowReg] = useState(false);
  const [regDone, setRegDone] = useState(null); // registration result
  const [ticketQr, setTicketQr] = useState("");

  // Build a scannable QR for the gate. The check-in scanner reads the "reg="
  // and "token=" params out of the encoded URL, so we encode the ticket URL.
  // qrcode-generator is pure JS (no Buffer / node deps) and returns a data URL
  // synchronously, so it works reliably in the browser bundle.
  useEffect(() => {
    if (!regParam || !tokenParam) { setTicketQr(""); return; }
    try {
      const ticketUrl = `${window.location.origin}/6b345a51df60fed6f07c5ff42e1853d0?id=${encodeURIComponent(id)}&reg=${encodeURIComponent(regParam)}&token=${encodeURIComponent(tokenParam)}`;
      const qr = qrcode(0, "M");           // typeNumber 0 = auto-size, ECC level M
      qr.addData(ticketUrl);
      qr.make();
      setTicketQr(qr.createDataURL(6, 12)); // cellSize=6px, margin=12px
    } catch {
      setTicketQr("");
    }
  }, [id, regParam, tokenParam]);

  if (ev.loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
  if (!data || data.success === false) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="text-muted-foreground max-w-sm">
        {data?.message || "This event isn't published yet, or the link is invalid."}
      </div>
      <Link to="/events">
        <Button variant="outline" size="sm" className="gap-1.5"><ArrowLeft className="h-4 w-4" />Back to events</Button>
      </Link>
    </div>
  );

  const regOpen   = data.regStatus?.open;
  const typeClass = TYPE_COLOR[data.eventType] || TYPE_COLOR.other;

  // Show QR ticket if ?reg=&token= in URL (redirected from notification email)
  const showTicket = regParam && tokenParam;

  return (
    <div className="min-h-screen pb-16">
      {/* Hero banner */}
      <div className="relative h-52 sm:h-72 overflow-hidden bg-gradient-to-br from-primary to-primary/60">
        {data.bannerUrl && <img src={data.bannerUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute top-4 left-4">
          <Link to="/events">
            <button className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm glass px-3 py-1.5 rounded-lg">
              <ArrowLeft className="h-4 w-4" />Back
            </button>
          </Link>
        </div>
        <div className="absolute bottom-5 left-5 right-5">
          {data.eventType && (
            <span className={cn("text-[10px] font-bold px-2.5 py-0.5 rounded-full capitalize mb-2 inline-block", typeClass)}>
              {data.eventType.replace(/_/g, " ")}
            </span>
          )}
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight">{data.title}</h1>
          {data.tagline && <p className="text-white/75 text-sm mt-1">{data.tagline}</p>}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* QR Ticket (when opened from confirmation email) */}
        {showTicket && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-5 border border-green-200 dark:border-green-800/40 bg-green-50/50 dark:bg-green-950/20 space-y-3">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-semibold">
              <CheckCircle2 className="h-5 w-5" />Your registration is confirmed
            </div>
            {ticketQr && (
              <div className="flex flex-col items-center gap-2 py-2">
                <div className="bg-white p-3 rounded-xl border border-green-200 dark:border-green-800/40">
                  <img src={ticketQr} alt="Entry QR code" className="w-44 h-44" style={{ imageRendering: "pixelated" }} />
                </div>
                <p className="text-[11px] text-muted-foreground text-center">Show this QR at the entrance gate for check-in</p>
              </div>
            )}
            <div className="text-xs text-muted-foreground font-mono text-center">{regParam}</div>
            <div className="flex flex-wrap gap-2">
              {data.waGroupLink && (
                <a href={data.waGroupLink} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" className="gap-1.5 bg-green-600 hover:bg-green-700"><MessageCircle className="h-3.5 w-3.5" />Join WhatsApp group</Button>
                </a>
              )}
              <a href={`/my-events`}>
                <Button size="sm" variant="outline" className="gap-1.5"><QrCode className="h-3.5 w-3.5" />My events</Button>
              </a>
            </div>
          </motion.div>
        )}

        {/* Event meta */}
        <div className="glass rounded-2xl p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {data.date && (
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div><div className="font-medium">{fmtDate(data.date)}</div><div className="text-xs text-muted-foreground">{data.timeSlot}</div></div>
              </div>
            )}
            {(data.hallName || data.venueText || data.hallLocation) && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div><div className="font-medium">{data.hallName || data.venueText}</div><div className="text-xs text-muted-foreground">{data.hallLocation || data.venueText}</div></div>
              </div>
            )}
            {data.seatCap > 0 && (
              <div className="flex items-start gap-2">
                <Users className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div><div className="font-medium">{data.seatsAvailable ?? data.seatCap} seats left</div><div className="text-xs text-muted-foreground">of {data.seatCap} total</div></div>
              </div>
            )}
            {data.regStatus && (
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <Badge status={regOpen ? "confirmed" : "cancelled"} label={regOpen ? "Registration open" : (data.regStatus.reason || "Registration closed")} />
                </div>
              </div>
            )}
          </div>
          {data.extLink && (
            <a href={data.extLink} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center gap-1.5 text-sm text-primary hover:underline">
              <ExternalLink className="h-3.5 w-3.5" />{data.extLink}
            </a>
          )}
        </div>

        {/* Registration CTA */}
        {data.regEnabled && !showTicket && !regDone && (
          <div>
            {regOpen ? (
              <>
                <Button onClick={() => setShowReg(v => !v)} className="w-full gap-2" size="lg">
                  <Award className="h-4 w-4" />{showReg ? "Cancel" : "Register now"}
                </Button>
                <AnimatePresence>
                  {showReg && (
                    <RegistrationForm
                      bookingID={id}
                      ev={data}
                      onSuccess={result => { setShowReg(false); setRegDone(result); toast.success(result.message || "Registered!"); }}
                    />
                  )}
                </AnimatePresence>
              </>
            ) : (
              <div className="glass rounded-xl p-4 text-center text-sm text-muted-foreground">
                {data.regStatus?.reason || "Registration is closed."}
              </div>
            )}
          </div>
        )}

        {/* Registration success */}
        {regDone && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-5 border border-green-200 dark:border-green-800/40 bg-green-50/50 dark:bg-green-950/20 space-y-3">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-semibold">
              <CheckCircle2 className="h-5 w-5" />{regDone.message || "Registration successful!"}
            </div>
            <div className="text-xs font-mono text-muted-foreground">{regDone.regID}</div>
            {regDone.status === "waitlist" && <p className="text-xs text-muted-foreground">You are on the waitlist. We'll email you if a spot opens up.</p>}
            {data.waGroupLink && regDone.status === "confirmed" && (
              <a href={data.waGroupLink} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="gap-1.5 bg-green-600 hover:bg-green-700"><MessageCircle className="h-3.5 w-3.5" />Join WhatsApp group</Button>
              </a>
            )}
          </motion.div>
        )}

        {/* Description */}
        {data.description && (
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold mb-2">About this event</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{data.description}</p>
          </div>
        )}

        {/* Speaker */}
        {data.speakerName && (
          <div className="glass rounded-2xl p-5 flex items-center gap-4">
            {data.speakerPhoto
              ? <img src={data.speakerPhoto} alt="" className="w-14 h-14 rounded-full object-cover shrink-0" />
              : <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary text-xl shrink-0">{data.speakerName[0]}</div>
            }
            <div>
              <div className="font-semibold">{data.speakerName}</div>
              <div className="text-sm text-muted-foreground">{data.speakerDesg}</div>
            </div>
          </div>
        )}

        {/* Coordinators */}
        {(data.coordinators || []).filter(c => c.name).length > 0 && (
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold mb-3">Coordinators</h3>
            <div className="space-y-2">
              {(data.coordinators || []).filter(c => c.name).map((c, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">{c.name[0]}</div>
                  <div>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{[c.phone, c.email].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Agenda */}
        {(data.agenda || []).filter(s => s.title).length > 0 && (
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold mb-4">Schedule</h3>
            <div className="space-y-2">
              {(data.agenda || []).filter(s => s.title).map((s, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-border/30 last:border-0">
                  <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full shrink-0 mt-0.5 tabular-nums">{s.time || "—"}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{s.title}</div>
                    {s.speaker && <div className="text-xs text-muted-foreground">{s.speaker}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gallery */}
        {galleryPhotos.length > 0 && (
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold mb-3">Gallery</h3>
            <div className="grid grid-cols-3 gap-2">
              {galleryPhotos.map((photo, i) => (
                <a key={photo.photo_id || i} href={photo.public_url} target="_blank" rel="noopener noreferrer"
                  className="aspect-square rounded-xl overflow-hidden bg-muted block">
                  <img src={photo.thumbnail_url || photo.public_url} alt={photo.caption || ""} loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Feedback link (post-event) */}
        {data.isClosed && (
          <div className="text-center">
            <a href={`/feedback?event=${id}`}>
              <Button variant="outline" className="gap-2"><Star className="h-4 w-4" />Leave feedback</Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function RegistrationForm({ bookingID, ev, onSuccess }) {
  const [form, setForm] = useState({ name:"", email:"", usn:"", college_id:"", dept:"", year:"", phone:"" });
  const [teamName, setTeamName]   = useState("");
  const [teamMembers, setTeamMembers] = useState("");
  const [answers, setAnswers]     = useState({});
  const [busy, setBusy]           = useState(false);

  const enabledFields = (ev.formFields || []).map(f => f.id || f);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const YEARS = ["1st Year","2nd Year","3rd Year","4th Year","Faculty","Other"];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const formData = { ...form, teamName: teamName || undefined, teamMembers: teamMembers || undefined, customAnswers: Object.keys(answers).length > 0 ? answers : undefined };
      const r = await api.register(bookingID, formData);
      onSuccess(r);
    } catch(err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const visible = (id) => !enabledFields.length || enabledFields.includes(id);

  return (
    <motion.form onSubmit={submit} initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }}
      className="glass rounded-2xl p-5 mt-3 space-y-4">
      <h3 className="font-semibold">Registration details</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        {visible("name")       && <div className="space-y-1.5"><Label>Full name *</Label><Input value={form.name}       onChange={f("name")}       placeholder="Your full name" /></div>}
        {visible("email")      && <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={form.email} onChange={f("email")} placeholder={ev.emailDomainRequired ? `@${ev.emailDomainRequired}` : "your@email.com"} /></div>}
        {visible("usn")        && <div className="space-y-1.5"><Label>USN</Label><Input value={form.usn}        onChange={f("usn")}        placeholder="1BM22CS001" className="font-mono" /></div>}
        {visible("college_id") && <div className="space-y-1.5"><Label>College ID</Label><Input value={form.college_id} onChange={f("college_id")} placeholder="College ID" /></div>}
        {visible("dept")       && <div className="space-y-1.5"><Label>Department</Label><Input value={form.dept}  onChange={f("dept")}       placeholder="CSE, ECE, AIML…" /></div>}
        {visible("year")       && (
          <div className="space-y-1.5"><Label>Year / Semester</Label>
            <Select value={form.year} onValueChange={v => setForm(p => ({ ...p, year: v }))}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {visible("phone")      && <div className="space-y-1.5"><Label>Phone</Label><Input type="tel" value={form.phone} onChange={f("phone")} placeholder="+91 9XXXXXXXXX" /></div>}
      </div>

      {/* Team registration */}
      {ev.teamRegEnabled && (
        <div className="space-y-3 border-t border-border/40 pt-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Team details</p>
          <div className="space-y-1.5"><Label>Team name *</Label><Input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="Team Innovators" /></div>
          <div className="space-y-1.5"><Label>Other team members <span className="text-muted-foreground font-normal">(names, comma separated)</span></Label>
            <Input value={teamMembers} onChange={e => setTeamMembers(e.target.value)} placeholder="Jane Doe, John Smith" />
          </div>
        </div>
      )}

      {/* Custom questions */}
      {(ev.customQs || []).length > 0 && (
        <div className="space-y-3 border-t border-border/40 pt-3">
          {(ev.customQs || []).map((q, i) => (
            <div key={i} className="space-y-1.5">
              <Label>{q.question}{q.required && " *"}</Label>
              {q.type === "yesno" ? (
                <Select value={answers[`q${i}`] || ""} onValueChange={v => setAnswers(p => ({ ...p, [`q${i}`]: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                </Select>
              ) : (
                <Input value={answers[`q${i}`] || ""} onChange={e => setAnswers(p => ({ ...p, [`q${i}`]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>
      )}

      <Button type="submit" className="w-full" size="lg" disabled={busy}>
        {busy ? "Registering…" : "Confirm registration"}
      </Button>
      {ev.confirmMsg && <p className="text-xs text-center text-muted-foreground">{ev.confirmMsg}</p>}
    </motion.form>
  );
}
